import React, { useState } from 'react';
import { Button, Input, Modal, Table, message, InputNumber, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';

const ProductSelector = ({ onAddProduct, currentShopId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [quantities, setQuantities] = useState({});

  const handleSearch = async () => {
    if (!searchText.trim()) {
      message.warning('Please enter search text');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/products', {
        params: {
          name: searchText,
          include_stock: true,
        },
      });
    
      setProducts(response.data.products);
      
      // Initialize quantities
      const initialQuantities = response.data.products.reduce((acc, product) => {
        acc[product.id] = 1;
        return acc;
      }, {});
      setQuantities(initialQuantities);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to search products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityChange = (productId, value) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleAddProduct = (product) => {
    const selectedQuantity = quantities[product.id];

    if (product.quantity < 1) {
      message.error('Cannot add out-of-stock product');
      return;
    }

    if (!selectedQuantity || selectedQuantity < 1) {
      message.error('Please enter a valid quantity');
      return;
    }

    if (selectedQuantity > product.quantity) {
      message.error('Quantity cannot exceed available stock');
      return;
    }

    onAddProduct({
      ...product,
      quantity: selectedQuantity,
    });
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Stock',
      dataIndex: 'quantity',
      key: 'stock',
      render: (stock) => (
        <Tag color={stock > 0 ? 'green' : 'red'}>
          {stock > 0 ? `${stock} available` : 'Out of stock'}
        </Tag>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (_, record) => (
        <InputNumber
          min={1}
          max={record.quantity}
          defaultValue={1}
          disabled={record.quantity < 1}
          onChange={(value) => handleQuantityChange(record.id, value)}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleAddProduct(record)}
          disabled={record.quantity < 1}
        >
          Add
        </Button>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 16 }}>
      <Button
        type="dashed"
        onClick={() => setIsModalVisible(true)}
        icon={<SearchOutlined />}
      >
        Add Products
      </Button>

      <Modal
        title="Select Products"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ display: 'flex', marginBottom: 16 }}>
          <Input
            placeholder="Search by product name or barcode"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ flex: 1, marginRight: 8 }}
          />
          <Button
            type="primary"
            onClick={handleSearch}
            loading={isLoading}
          >
            Search
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 5 }}
        />
      </Modal>
    </div>
  );
};

export default ProductSelector;